import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Store } from '@ngxs/store';
import {
  OrderApi,
  CashDrawerApi,
  PaymentApi,
  OrderDTO,
  OrderItemDTO,
  PaymentDTO,
  CashDrawerActionDTOCashDrawerActionTypeEnum,
} from '@lkp/parking-sdk';
import {
  ORDER_API,
  CASH_DRAWER_API,
  PAYMENT_API,
} from '../../core/providers/api.module';
import { Subject, first, takeUntil } from 'rxjs';
import { CashPaymentComponent } from 'src/app/components/payment/cash-payment/cash-payment.component';
import { KlarnaRefundComponent } from 'src/app/components/payment/klarna-refund/klarna-refund.component';
import { ManualRefundComponent } from 'src/app/components/payment/manual-refund/manual-refund.component';
import { PaxPaymentComponent } from 'src/app/components/payment/pax-payment/pax-payment.component';
import { DateService } from 'src/app/core/services/date.service';
import { OrdersState } from 'src/app/core/states/orders/orders.state';
import { GetOrder } from 'src/app/core/states/orders/orders.action';
import { GetBooking } from 'src/app/core/states/bookings/booking.actions';
import { Router } from '@angular/router';

@Component({
    selector: 'app-refund',
    templateUrl: './refund.component.html',
    styleUrls: ['./refund.component.scss'],
    standalone: false
})
export class RefundComponent implements OnDestroy {
  order: OrderDTO | undefined;
  refundItem: FormControl = new FormControl<OrderItemDTO | undefined>(
    undefined
  );
  paymentFinished: boolean = true;
  newOrder: boolean = false;
  allowPayments = true;

  bookingId?: number;
  prepaidExists: boolean = false;
  refundExists: boolean = false;
  startedPayments?: PaymentDTO[];

  @ViewChild('iframe', { read: ElementRef, static: false }) iframe:
    | ElementRef
    | undefined;

  readonly onDestroy$ = new Subject<void>();

  constructor(
    readonly store: Store,
    readonly dialog: MatDialog,
    readonly snackBar: MatSnackBar,
    readonly cdr: ChangeDetectorRef,
    @Inject(ORDER_API) readonly orderApi: OrderApi,
    @Inject(CASH_DRAWER_API) readonly cashDrawerApi: CashDrawerApi,
    @Inject(PAYMENT_API) readonly paymentApi: PaymentApi,
    readonly dateService: DateService,
    readonly router: Router
  ) {
    this.store
      .select(OrdersState.order)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((curOrder) => {
        this.order = curOrder;
        this.refundExists = !!this.order.payments?.some(
          (payment) => payment.transactionType === 'REFUND'
        );
        this.prepaidExists = false;
        this.bookingId = undefined;
        this.order.orderItems?.forEach((item) => {
          if (item.booking && !this.bookingId) {
            this.bookingId = item.booking.bookingId;
          }
          if (item.prepaidTicket) {
            this.prepaidExists = true;
          }
        });
      });
  }

  ngOnInit() {
    this.getUnfinishedPayments();
    this.reloadOrder();
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  reloadOrder(orderId = this.order?.orderId) {
    if (orderId) {
      return this.store.dispatch(new GetOrder(orderId));
    }
    return undefined;
  }

  handleSuccessfulRefund(orderId?: number, receipt = false) {
    const finishRefund = () => {
      this.paymentFinished = true;
      this.refundItem.setValue(undefined);
      if (receipt) this.printReceipt();
      this.snackBar.open('Återbetalning genomförd', 'OK', {
        duration: 3000,
      });
    };

    const reloadOrder = this.reloadOrder(orderId);
    if (reloadOrder) {
      reloadOrder.pipe(first(), takeUntil(this.onDestroy$)).subscribe(() => {
        finishRefund();
      });
    } else {
      finishRefund();
    }
  }

  refundPayment(payment: PaymentDTO) {
    this.paymentFinished = false;
    const orderId = this.order?.orderId;

    let maxRefund =
      this.order?.amountToPay != undefined
        ? Math.abs(this.order.amountToPay)
        : 0;

    // Refund for cash
    if (payment.paymentMethod!.paymentMethodId === 'CASH') {
      const dialogRef = this.dialog.open(CashPaymentComponent, {
        width: '30vw',
        data: {
          order: this.order,
          refund: payment,
          maxRefund: maxRefund,
          orderItemId: this.refundItem.value.orderItemId,
        },
      });

      dialogRef
        .afterClosed()
        .pipe(takeUntil(this.onDestroy$))
        .subscribe((result) => {
          if (result?.success) {
            this.handleSuccessfulRefund(orderId, result.receipt);
          }
        });
    }
    // Refund for card
    else if (payment.paymentMethod?.paymentMethodId === 'PAX') {
      const dialogRef = this.dialog.open(PaxPaymentComponent, {
        width: '30vw',
        data: {
          order: this.order,
          refund: payment,
          maxRefund: maxRefund,
          orderItemId: this.refundItem.value.orderItemId,
        },
      });

      dialogRef
        .afterClosed()
        .pipe(takeUntil(this.onDestroy$))
        .subscribe((result) => {
          if (result?.success) {
            this.handleSuccessfulRefund(orderId, result.receipt);
          } else if (result && result.cancelled) {
            this.snackBar.open('Order avbruten', 'OK', { duration: 3000 });
          } else if (result && result.declined) {
            this.snackBar.open('Medges ej - prova igen', 'OK', {
              duration: 10000,
            });
          } else if (result?.unavailable) {
            this.snackBar.open('Kunde inte ansluta till terminalen', 'OK', {
              duration: 10000,
            });
          }
        });
    }
    // Refund for Klarna
    else if (payment.paymentMethod?.paymentMethodId === 'KLARNA') {
      const dialogRef = this.dialog.open(KlarnaRefundComponent, {
        width: '30vw',
        data: {
          order: this.order,
          refund: payment,
          maxRefund: maxRefund,
          orderItemId: this.refundItem.value.orderItemId,
        },
      });

      dialogRef
        .afterClosed()
        .pipe(takeUntil(this.onDestroy$))
        .subscribe((result) => {
          if (result?.success) {
            this.handleSuccessfulRefund(orderId);
          } else {
            this.snackBar.open('Någonting gick fel, försök igen', 'OK', {
              duration: 3000,
            });
          }
        });
    } else if (
      payment.paymentMethod?.paymentMethodId === 'BANK' ||
      payment.paymentMethod?.paymentMethodId === 'INVOICE'
    ) {
      const dialogRef = this.dialog.open(ManualRefundComponent, {
        width: '30vw',
        data: {
          order: this.order,
          refund: payment,
          maxRefund: maxRefund,
          orderItemId: this.refundItem.value.orderItemId,
        },
      });

      dialogRef
        .afterClosed()
        .pipe(takeUntil(this.onDestroy$))
        .subscribe((result) => {
          if (result?.success) {
            this.handleSuccessfulRefund(orderId);
          } else {
            this.snackBar.open('Återbetalning avbruten', 'OK', {
              duration: 3000,
            });
          }
        });
    }
  }

  goToBooking(bookingId?: number) {
    if (bookingId) {
      this.store
        .dispatch(new GetBooking(bookingId))
        .pipe(takeUntil(this.onDestroy$))
        .subscribe(() => {
          this.router.navigate(['bokning']);
        });
    }
  }

  getUnfinishedPayments() {
    this.order?.orderId &&
      this.orderApi
        .getUnfinishedPayments({ orderId: this.order.orderId })
        .pipe(first())
        .subscribe((response) => {
          if (response?.length) {
            this.startedPayments = response;
            this.allowPayments = false;
          } else {
            this.startedPayments = undefined;
            this.allowPayments = true;
          }
        });
  }

  isDisabled(item: OrderItemDTO): boolean {
    return item.amountPaid === 0;
  }

  formatDate(date?: string | Date): string | undefined {
    return date && this.dateService.formatDate(date);
  }

  formatTime(date?: string | Date): string | undefined {
    return date && this.dateService.formatTime(date);
  }

  awaitReceiptChanges(): Promise<void> {
    return new Promise<void>((resolve) => {
      this.cdr.detectChanges();
      setTimeout(() => {
        resolve();
      }, 0);
    });
  }

  printReceipt() {
    this.cashDrawerApi
      .addCashDrawerActionAdmin({
        cashDrawerActionDTO: {
          cashDrawerActionType:
            CashDrawerActionDTOCashDrawerActionTypeEnum.Payment,
          comment: 'Refund',
        },
      })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe();

    let latestPayment =
      (this.order?.payments?.length &&
        this.order.payments[this.order.payments.length - 1]) ||
      undefined;

    latestPayment?.paymentId &&
      this.paymentApi
        .getPaymentReceiptHtmlAdmin({ paymentId: latestPayment.paymentId })
        .pipe(takeUntil(this.onDestroy$))
        .subscribe((v) => {
          if (v.html) {
            if (this.iframe) {
              this.iframe.nativeElement.contentWindow.document.open();
              this.iframe.nativeElement.contentWindow.document.close();
              this.iframe.nativeElement.contentWindow.frames.document.body.insertAdjacentHTML(
                'afterbegin',
                v.html
              );
              this.awaitReceiptChanges().then(() => {
                this.iframe?.nativeElement.contentWindow.print();
              });
            }
          }
        });
  }
}
